import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { X } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { desktopBridge } from "../lib/desktop";
import { getServiceWorkerUpdateAvailable, subscribeToServiceWorkerUpdate } from "../lib/pwa";
import { rpc } from "../lib/rpc";
import {
  checkWebPush,
  disableWebPush,
  enableWebPush,
  readWebPushPromptDismissed,
  rememberWebPushPromptDismissed,
  syncWebPushRegistration,
  webPushSupport,
} from "../lib/web-push";
import { isStandaloneApp } from "./PwaInstall";

export const WEB_PUSH_PROMPT_TEST_ID = "web-push-prompt";

type PromptStatus = "loading" | "hidden" | "off" | "denied" | "enabled" | "unsupported" | "error";

/**
 * The one-time offer to receive a bot's reply on an installed web app. It stays out of the way
 * everywhere else: desktop browsers, the Electron app, and a deployment whose worker has no
 * VAPID key. The same card reports a blocked permission and carries the disable control.
 */
export function WebPushNotifications() {
  const { t } = useLingui();
  const updateAvailable = useSyncExternalStore(
    subscribeToServiceWorkerUpdate,
    getServiceWorkerUpdateAvailable,
    () => false,
  );
  const [status, setStatus] = useState<PromptStatus>("loading");
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (desktopBridge() || !isStandaloneApp()) return hide();
      const dismissed = readWebPushPromptDismissed();
      const support = webPushSupport({
        secureContext: window.isSecureContext,
        serviceWorker: "serviceWorker" in navigator,
        pushManager: "PushManager" in window,
        notification: "Notification" in window,
        userAgent: navigator.userAgent,
        standalone: true,
      });
      if (!support.supported) {
        if (support.reason === "ios-version" && !dismissed) setStatus("unsupported");
        else hide();
        return;
      }
      let ready: ServiceWorkerRegistration;
      try {
        ready = await navigator.serviceWorker.ready;
      } catch {
        hide();
        return;
      }
      if (cancelled) return;
      setRegistration(ready);
      const state = await checkWebPush(ready, window.Notification);
      if (cancelled) return;
      if (state === "enabled") {
        void syncWebPushRegistration({
          registration: ready,
          permission: window.Notification,
          registerDevice: (input) => rpc.notifications.registerDevice(input),
        });
      }
      if (dismissed) hide();
      else setStatus(state);
    })();
    return () => {
      cancelled = true;
    };

    function hide() {
      if (!cancelled) setStatus("hidden");
    }
  }, []);

  if (updateAvailable || status === "loading" || status === "hidden") return null;

  const copy =
    status === "off"
      ? {
          title: t`Get notified when a bot replies`,
          detail: error ?? t`A notice arrives even when HrokBot is closed.`,
        }
      : status === "denied"
        ? {
            title: t`Notifications are blocked`,
            detail: t`Allow them in your browser settings.`,
          }
        : status === "enabled"
          ? {
              title: t`Notifications are on`,
              detail: error ?? t`This device gets a notice when a bot replies.`,
            }
          : status === "unsupported"
            ? {
                title: t`Notifications need iOS 16.4 or later`,
                detail: t`Update this iPhone to receive bot replies.`,
              }
            : {
                title: t`Notifications are unavailable`,
                detail: error ?? t`Try again later.`,
              };
  return (
    <aside
      aria-label={copy.title}
      aria-live="polite"
      data-testid={WEB_PUSH_PROMPT_TEST_ID}
      className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-border bg-card p-3 text-card-foreground shadow-lg sm:inset-x-auto sm:end-4 sm:mx-0"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-medium text-foreground">{copy.title}</p>
        <p className="mt-1 text-[12.5px] text-muted-foreground">{copy.detail}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {status === "off" ? (
          <Button
            size="sm"
            className="rounded-full"
            disabled={busy || !registration}
            onClick={() => void enable()}
          >
            {busy ? <Trans>Enabling…</Trans> : <Trans>Enable</Trans>}
          </Button>
        ) : status === "enabled" ? (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            disabled={busy || !registration}
            onClick={() => void disable()}
          >
            {busy ? <Trans>Disabling…</Trans> : <Trans>Disable</Trans>}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t`Dismiss`}
          onClick={dismiss}
          disabled={busy}
        >
          <X size={15} strokeWidth={1.8} />
        </Button>
      </div>
    </aside>
  );

  async function enable() {
    if (!registration || busy) return;
    setBusy(true);
    setError(null);
    const result = await enableWebPush({
      registration,
      permission: window.Notification,
      fetchPublicKey: async () => (await rpc.notifications.webPushKey()).publicKey,
      registerDevice: (input) => rpc.notifications.registerDevice(input),
    });
    setBusy(false);
    if (result.status === "enabled") {
      setStatus("enabled");
      return;
    }
    if (result.status === "denied") {
      setStatus("denied");
      return;
    }
    setStatus("error");
    setError(
      result.status === "unavailable"
        ? t`Notifications are not available on this server.`
        : t`Notifications could not be turned on.`,
    );
  }

  async function disable() {
    if (!registration || busy) return;
    setBusy(true);
    const ok = await disableWebPush({
      registration,
      unregisterDevice: (input) => rpc.notifications.unregisterDevice(input),
    });
    setBusy(false);
    setStatus("off");
    setError(ok ? null : t`Turned off here. The server could not be told.`);
  }

  function dismiss() {
    rememberWebPushPromptDismissed();
    setStatus("hidden");
  }
}
